import { inject, Injectable } from "@angular/core";
import { catchError, concatMap, EMPTY, map, Observable, of, switchMap, tap } from "rxjs";
import { IPC_CHANNEL } from "../../../_offline/electron.constants";
import { MigrationStateSchema } from "../../../_offline/models/migration-state.schema";
import { EvcModalService } from "../../../_shared/services/modal/evc-modal.service";
import { ElectronService } from "../../../electron.service";
import { IndividualReportTemplateDbService } from "../../database/individual-report-template-db.service";
import { InspectionDbService } from "../../database/inspection-db.service";
import { FeatureFlagService } from "../../feature-flag/feature-flag.service";
import { Log } from "./log.decorator";
import { MigrationProgressModalComponent } from "./migration-progress-modal/migration-progress-modal.component";
import { MigrationFailureModalComponent } from "./migration-failure-modal/migration-failure-modal.component";
import { CreatedPropertyDictionnary, PropertiesMigrationService } from "./properties-migration.service";
import { SubjectModelMigration } from "./subject-models-migration.service";
import { SubjectsMigrationService } from "./subjects-migration.service";
import { IdsMap } from "./types";
import { AssetModuleClient } from "../asset-module-client.service";

const MIGRATION_STATE_KEY = "standalone-asset-module-migration";
export type MigrationState = "NotStarted" | "InProgress" | "Completed" | "Failed";

type ReportTemplateMigrationOutput = { templateId: string; oldSubjectModelIds: string[]; assetTypeIds: string[] };

@Injectable({
  providedIn: "root"
})
export class StandaloneMigrationOrchestrator {
  private electronService: ElectronService = inject(ElectronService);
  private subjectModelMigration: SubjectModelMigration = inject(SubjectModelMigration);
  private featureFlagService: FeatureFlagService = inject(FeatureFlagService);
  private modalService: EvcModalService = inject(EvcModalService);
  private propertiesMigrationService: PropertiesMigrationService = inject(PropertiesMigrationService);
  private subjectsMigration: SubjectsMigrationService = inject(SubjectsMigrationService);
  private inspectionDbService: InspectionDbService = inject(InspectionDbService);
  private reportTemplateDbService: IndividualReportTemplateDbService = inject(IndividualReportTemplateDbService);
  private assetModuleClient: AssetModuleClient = inject(AssetModuleClient);

  public migrate() {
    const flag = this.featureFlagService.isFlagEnabled("asset_module")();
    if (!flag) {
      return EMPTY;
    }

    return this.getMigrationState().pipe(
      switchMap((state) => {
        if (state === "Completed") {
          return EMPTY;
        }
        if (state === "Failed") {
          return this.showFailureModal();
        }
        return this.runMigration();
      })
    );
  }

  private getMigrationState(): Observable<MigrationState> {
    return of(this.electronService.sendSyncCommand(IPC_CHANNEL.MIGRATION_STATE.GET, MIGRATION_STATE_KEY)).pipe(
      map((row: MigrationStateSchema | null) => (row ? (row.value as MigrationState) : "NotStarted"))
    );
  }

  private setMigrationState(state: MigrationState): Observable<MigrationState> {
    return of(this.electronService.sendSyncCommand(IPC_CHANNEL.MIGRATION_STATE.PUT, { key: MIGRATION_STATE_KEY, value: state })).pipe(
      map(() => state)
    );
  }

  @Log("runMigration")
  private runMigration(): Observable<MigrationState> {
    let properties: CreatedPropertyDictionnary;
    let subjectModelsIdsMap: IdsMap;
    const ref = this.modalService.showModal(MigrationProgressModalComponent);

    return this.setMigrationState("InProgress").pipe(
      concatMap(() => this.propertiesMigrationService.migrateSystemProperties()),
      tap((propertiesCreated) => (properties = propertiesCreated)),
      concatMap(() => this.subjectModelMigration.migrateSubjectModels()),
      tap((ids) => (subjectModelsIdsMap = ids)),
      concatMap(() => this.subjectsMigration.migrateSubjects(subjectModelsIdsMap, properties)),
      concatMap((subjectsIdsMap) => this.relinkInspections(subjectsIdsMap)),
      concatMap(() => this.relinkReportTemplates(subjectModelsIdsMap)),
      concatMap(() => {
        ref.componentRef?.instance.isCompleted.set(true);
        return this.setMigrationState("Completed");
      }),
      catchError(() => {
        ref.close();
        return this.setMigrationState("Failed").pipe(
          switchMap(() => {
            return this.showFailureModal();
          })
        );
      })
    );
  }

  private showFailureModal() {
    const failureRef = this.modalService.showModal(MigrationFailureModalComponent);
    return failureRef.afterClosed.pipe(
      switchMap(() => this.assetModuleClient.deleteAssetDataBundle()),
      switchMap(() => this.runMigration())
    );
  }

  @Log("relinkInspections")
  private relinkInspections(subjectsIdsMap: IdsMap): Observable<{ inspectionId: string; liveAssetId: string; subjectId: string }[]> {
    return this.inspectionDbService.getAllOnPremise().pipe(
      map((inspections) =>
        inspections.map((inspection) => {
          const version = inspection.attributesVersion ?? 1;
          const newLiveAssetId = subjectsIdsMap.get(`${inspection.subjectId}${version}`);
          if (newLiveAssetId) {
            inspection.liveAssetId = newLiveAssetId;
          }
          return inspection;
        })
      ),
      switchMap((inspections) => this.inspectionDbService.putListAll(inspections)),
      map((inspections) => inspections.map((i) => ({ inspectionId: i.id, subjectId: i.subjectId, liveAssetId: i.liveAssetId })))
    );
  }

  @Log("relinkReportTemplates")
  private relinkReportTemplates(subjectModelIdsMap: IdsMap): Observable<ReportTemplateMigrationOutput[]> {
    return this.reportTemplateDbService.getAllOnPremise().pipe(
      map((reportTemplates) =>
        reportTemplates.map((template) => {
          const oldSubjectModelIds = JSON.parse(template.subjectModelIds) as string[];
          const assetTypeModelIds = oldSubjectModelIds.map((subjectModelId) => subjectModelIdsMap.get(subjectModelId)).filter((ids) => !!ids);
          template.assetTypeIds = JSON.stringify(assetTypeModelIds);
          return { template, oldSubjectModelIds, assetTypeModelIds };
        })
      ),
      switchMap((items) =>
        this.reportTemplateDbService.putAll(items.map((i) => i.template)).pipe(
          map(() =>
            items.map((item) => ({
              templateId: item.template.id,
              oldSubjectModelIds: item.oldSubjectModelIds,
              assetTypeIds: item.assetTypeModelIds
            }))
          )
        )
      )
    );
  }
}
